import React, { useState } from 'react';
import { ArrowLeftIcon, FunnelIcon, EyeIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import LoadingSpinner from '../components/UI/LoadingSpinner';

interface MedicineLog {
  id: string;
  pond_id: string;
  medicine_date: string;
  medicine_type: string;
  quantity: number;
  unit: string;
  remarks?: string;
  created_at: string;
  created_by: string;
  pond_name?: string;
}

const MedicineLogs: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  
  // Set default date range to last 7 days
  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    return {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };
  
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [selectedPond, setSelectedPond] = useState('');
  const [selectedMedicineType, setSelectedMedicineType] = useState('');

  // Build the API endpoint with query parameters
  const buildMedicineLogsEndpoint = () => {
    if (!selectedLocation) return null;
    
    let endpoint = `/locations/${selectedLocation.id}/medicine-logs`;
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (selectedPond) params.push(`pond_id=${selectedPond}`);
    if (selectedMedicineType) params.push(`medicine_type=${selectedMedicineType}`);
    
    if (params.length > 0) {
      endpoint += `?${params.join('&')}`;
    }
    
    return endpoint;
  };

  const { data: pondsData } = useApi(`/locations/${selectedLocation?.id}/ponds`);
  const { data: medicineTypesData } = useApi<any[]>(
    selectedLocation?.id ? `/master-data/medicine_types?location_id=${encodeURIComponent(selectedLocation.id)}` : '',
    {
      dependencies: [selectedLocation?.id],
      skip: !selectedLocation
    }
  );
  const { data: medicineLogsData, loading, error, refetch } = useApi<MedicineLog[]>(
    buildMedicineLogsEndpoint() || '',
    { 
      dependencies: [selectedLocation?.id, startDate, endDate, selectedPond, selectedMedicineType],
      skip: !selectedLocation
    }
  );

  // Debug logging
  React.useEffect(() => {
    console.log('MedicineLogs Debug:', {
      selectedLocation: selectedLocation?.id,
      endpoint: buildMedicineLogsEndpoint(),
      medicineLogsData,
      error,
      loading,
      apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8103/api'
    });
  }, [selectedLocation?.id, medicineLogsData, error, loading]);

  // Enrich medicine logs with pond names
  const enrichedMedicineLogs = React.useMemo(() => {
    if (!medicineLogsData || !pondsData) return [];
    
    return medicineLogsData.map((log: MedicineLog) => ({
      ...log,
      pond_name: (pondsData as any[]).find((p: any) => p.id === log.pond_id)?.name || 'Unknown Pond'
    }));
  }, [medicineLogsData, pondsData]);

  const clearFilters = () => {
    const defaultDates = getDefaultDates();
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
    setSelectedPond('');
    setSelectedMedicineType('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalQuantity = () => {
    return enrichedMedicineLogs.reduce((sum, log) => sum + log.quantity, 0).toFixed(2);
  };

  const getUniqueMedicineTypes = () => {
    const types = Array.from(new Set(enrichedMedicineLogs.map(log => log.medicine_type)));
    return types.length;
  };

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-lg font-bold text-gray-800">💊 Medicine Logs</h1>
        </div>

        {/* Filters */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters</span>
            </div>
            <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
              Last 7 days
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Pond</label>
              <select
                value={selectedPond}
                onChange={(e) => setSelectedPond(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-xs"
              >
                <option value="">All Ponds</option>
                {(pondsData as any[] || []).map((pond: any) => (
                  <option key={pond.id} value={pond.id}>
                    {pond.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Medicine Type</label>
              <select
                value={selectedMedicineType}
                onChange={(e) => setSelectedMedicineType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-xs"
              >
                <option value="">All Types</option>
                {(medicineTypesData as any[] || []).map((medicine: any) => (
                  <option key={medicine.id} value={medicine.name}>
                    {medicine.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={clearFilters}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors"
            >
              Reset to 7 Days
            </button>
            <button
              onClick={refetch}
              className="flex-1 bg-red-600 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        {enrichedMedicineLogs.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="bg-red-50 p-2 rounded-lg">
              <div className="text-xs text-red-600 font-medium">Total Entries</div>
              <div className="text-sm font-bold text-red-700">{enrichedMedicineLogs.length}</div>
            </div>
            <div className="bg-orange-50 p-2 rounded-lg">
              <div className="text-xs text-orange-600 font-medium">Total Quantity</div>
              <div className="text-sm font-bold text-orange-700">{getTotalQuantity()} units</div>
            </div>
            <div className="bg-purple-50 p-2 rounded-lg">
              <div className="text-xs text-purple-600 font-medium">Medicine Types</div>
              <div className="text-sm font-bold text-purple-700">{getUniqueMedicineTypes()}</div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="p-8 text-center">
          <LoadingSpinner size="md" />
          <p className="text-gray-500 mt-2">Loading medicine logs...</p>
        </div>
      )}

      {/* Medicine Logs List */}
      {!loading && (
        <div className="p-4 space-y-3">
          {enrichedMedicineLogs.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">💊</div>
              <p className="text-gray-500 mb-2">No medicine logs found</p>
              <p className="text-sm text-gray-400">Try adjusting your filters or add some medicine entries.</p>
              {user?.role !== 'user' && (
                <Link 
                  to="/data-entry/medicine" 
                  className="inline-block mt-4 bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 transition-colors"
                >
                  Add Medicine Entry
                </Link>
              )}
            </div>
          ) : (
            enrichedMedicineLogs.map((log) => (
              <div key={log.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-800">{log.pond_name}</h3>
                    <p className="text-sm text-gray-500">{formatDate(log.medicine_date)}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-red-600">{log.quantity} {log.unit}</div>
                    <div className="text-xs text-gray-500">{log.medicine_type}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  {log.remarks && (
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-medium text-gray-600">Remarks:</span>
                      <span className="text-xs text-gray-800 flex-1">{log.remarks}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                    <span>Created: {formatDateTime(log.created_at)}</span>
                    <div className="flex items-center gap-1">
                      <EyeIcon className="h-3 w-3" />
                      <span>View Details</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Floating Action Button */}
      {user?.role !== 'user' && (
        <div className="fixed bottom-6 right-6">
          <Link
            to="/data-entry/medicine"
            className="bg-red-600 text-white p-4 rounded-full shadow-lg hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <span className="text-xl">💊</span>
            <span className="text-sm font-medium">Add Medicine</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default MedicineLogs;
